// SelectSearch.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Search, Check } from "lucide-react";

export default function SelectSearch({
  label,
  value,
  onChange,
  options = [], // [{ value, label, icon?: ReactComp }]
  placeholder = "Select…",
  searchable = true,
  noResults = "No results",
  loading = false,
  className = "",
}) {
  const btnRef = useRef(null);
  const popRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ACCENT = "#169FA7";

  const filtered = useMemo(() => {
    if (!q.trim()) return options;
    return options.filter((o) =>
      o.label.toLowerCase().includes(q.trim().toLowerCase())
    );
  }, [options, q]);

  const selected = useMemo(
    () => options.find((o) => o.value === value) || null,
    [options, value]
  );

  useEffect(() => {
    const onDown = (e) => {
      if (!open) return;
      if (!popRef.current || !btnRef.current) return;
      const t = e.target;
      if (
        !popRef.current.contains(t) &&
        !btnRef.current.contains(t)
      )
        setOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const menuVariants = {
    hidden: { opacity: 0, scale: 0.96, y: -6 },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: { type: "spring", stiffness: 420, damping: 32 },
    },
    exit: { opacity: 0, scale: 0.98, y: -4, transition: { duration: 0.12 } },
  };

  return (
    <div className={className}>
      {label && (
        <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 text-start mb-1">
          {label}
        </label>
      )}

      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={[
          "h-11 w-full rounded-full px-3 text-left",
          "bg-white dark:bg-neutral-900",
          "border border-neutral-200 dark:border-neutral-800",
          "text-neutral-900 dark:text-neutral-100",
          "focus:outline-none focus:ring-.5 focus:ring-[--ring] focus:border-[--ring]",
          "flex items-center justify-between",
        ].join(" ")}
        style={{ "--ring": ACCENT }}
      >
        <span className="truncate text-sm">
          {selected ? (
            selected.label
          ) : (
            <span className="text-neutral-400">{placeholder}</span>
          )}
        </span>
        <ChevronDown size={16} className="text-neutral-500" />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            ref={popRef}
            initial="hidden"
            animate="visible"
            exit="exit"
            variants={menuVariants}
            className="relative "
          >
            <div className="absolute z-50 mt-2 w-full rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white/95 dark:bg-neutral-900/95 backdrop-blur p-1.5 ">
              {searchable && (
                <div className="mb-1.5 flex items-center rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white/70 dark:bg-neutral-900/70 px-2">
                  <Search size={16} className="text-neutral-400" />
                  <input
                    autoFocus
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Search…"
                    className="ml-2 h-9 flex-1 bg-transparent text-sm outline-none placeholder:text-neutral-400"
                  />
                </div>
              )}

              <div className="max-h-56 overflow-auto">
                {loading ? (
                  <div className="px-3 py-2 text-sm text-neutral-500">
                    Loading…
                  </div>
                ) : filtered.length ? (
                  filtered.map((o) => {
                    const ActiveIcon = o.icon;
                    const isSel = o.value === value;
                    return (
                      <button
                        key={o.value}
                        type="button"
                        onClick={() => {
                          onChange?.(o.value, o);
                          setOpen(false);
                        }}
                        className={[
                          "w-full flex items-center gap-2 rounded-xl px-3 py-2 text-sm",
                          "hover:bg-neutral-100/70 dark:hover:bg-neutral-800/60",
                        ].join(" ")}
                      >
                        {ActiveIcon && (
                          <ActiveIcon size={16} className="text-neutral-500" />
                        )}
                        <span className="flex-1 text-left truncate">
                          {o.label}
                        </span>
                        {isSel && (
                          <Check
                            size={16}
                            className="text-[--ring]"
                            style={{ color: ACCENT }}
                          />
                        )}
                      </button>
                    );
                  })
                ) : (
                  <div className="px-3 py-2 text-sm text-neutral-500">
                    {noResults}
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
